import {Box, Button, Tooltip, Typography, useTheme} from "@mui/material";
import {clearProfilesMessages, deleteEntity, searchEntities} from './ProfilesReducer';
import React, {useCallback, useEffect, useState} from "react";
import {RootState} from "../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "notistack";
import ConfirmDialog from "../../../Shared/Components/ConfirmDialog";
import {ACard, ADataGrid, ADataGridColumn, ADataGridFilter, ASectionTitle, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {searchEntitiesData} from "./AuthorityReducer";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {
    AccountCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    MoreHorizOutlined
} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { hasPermission } from "../../../Shared/Auth/AuthenticationUtil";
import { AUTHORITIES } from "../../../Config/Constants";
import ActionsMenu, {ActionMenuItem} from "../../../Shared/Components/ActionsMenu";
import UserAssociationPanel from "./components/UserAssociationPanel";
import {QueryClientProvider} from "react-query";
import {sharedQueryClient} from "../../../queryClient";



const columns = [
    {
        name: 'id',
        label: 'ID',
        align: 'left',
        minWidth: 100,
        visibility: false,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'name',
        label: 'Perfil de acesso',
        align: 'left',
        minWidth: 100,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'userCount',
        label: 'Usuários associados',
        align: 'left',
        minWidth: 100,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'userCount', condition: 'equals', value: '', sort: 'none'}
    },
] as ADataGridColumn [];


export type ActionConfig = {
    actionName: string;
    disabled: boolean;
};

export interface RowProfile {
    id: number;
    name: string;
    userCount: number;
    actionsConfig?: Array<ActionConfig>
}

export function ProfilesSettingsHomePage(){
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [authorityGroupId, setAuthorityGroupId] = useState<any>(null);
    const [openDialog, setOpenDialog] = useState(false);

    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSaveProfile = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SAVE_PROFILE
    ]);
    const isAuthorizedToDeleteProfile = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.DELETE_PROFILE
    ]);
    const isAuthorizedToViewUsers = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.USER_AUTHORITY_GROUP_MANAGER, AUTHORITIES.VIEW_USER_AUTHORITIES_GROUP
    ])
    const isLoading = useAppSelector((state : RootState) => state.profile.loading);
    const errorMessage = useAppSelector((state : RootState) => state.profile.errorMessage);
    const entities = useAppSelector((state : RootState) => state.profile.entities);
    const totalOfRecords = useAppSelector((state : RootState) => state.profile.totalOfRecords);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(columns), 0, 100));

    const [rows, setRows] = useState<Array<RowProfile>>([]);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [selectedProfileForUsers, setSelectedProfileForUsers] = useState<any>(null);
    const [openUserAssociationPanel, setOpenUserAssociationPanel] = useState(false);
    const [selectedProfiles, setSelectedProfiles] = useState<Array<RowProfile>>([]);
    const [clearSelection, setClearSelection] = useState(false);

    const lastMousePosRef = React.useRef({ x: 0, y: 0 });


    useEffect(() => {
        let newRows = entities.map(et => {return {id: et.id, name: et.name, userCount: et.userCount ?? 0, actionsConfig: [
                {actionName: "edit", disabled: !isAuthorizedToSaveProfile},
                {actionName: "delete", disabled: !isAuthorizedToDeleteProfile},
            ]}});
            setRows(newRows)
        }, [entities]);


    const handleEditProfile = useCallback((data: any) => {
        navigate(SystemRoutes.SETTINGS_PROFILE_FORM, {state: {id: data.id}});
    },[navigate]);

    const deleteItemAction = useCallback((data: any) => {
        setAuthorityGroupId(data.id);
        setOpenDialog(true);
    },[]);

    const toggleDialog = () => {
        setOpenDialog(!openDialog);
    }

    const confirmDialog = async () => {
        try {
            await dispatch(deleteEntity(authorityGroupId)).unwrap();

            await dispatch(searchEntities(pagedSearchParams));
            setClearSelection(true);
            setSelectedProfiles([]);

            enqueueSnackbar("Perfil deletado com sucesso!", {
                variant: 'success',
            });
        } catch (error) {
            enqueueSnackbar("O Perfil não pode ser deletado porque já está em uso.", {
                variant: 'error',
            });
        } finally {
            toggleDialog();
        }
    };

    const closeDialog = () => {
        //TODO
        // @ts-ignore
        setAuthorityGroupId({});
        toggleDialog();
    }

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams :PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    },[]);

    const handleOnRowSelect = useCallback((selectedRows: Array<RowProfile>) => {
        setSelectedProfiles(selectedRows ?? []);
    },[]);

    // `onClearSelectedTempRows` da ADataGrid limpa a seleção na transição para true;
    // baixar a flag aqui a transforma num pulso, como em TeleObjectSettingsPage.
    useEffect(() => {
        if (clearSelection) {
            setClearSelection(false);
        }
    }, [clearSelection]);

    useEffect(() => {

        dispatch(searchEntitiesData(pagedSearchParams));
        if (!isLoading) {
            dispatch(searchEntities(pagedSearchParams));

        }
    },[dispatch, pagedSearchParams]);

    // O sucesso das ações desta tela é notificado no próprio handler (ex.: confirmDialog);
    // exibir também o successMessage do slice duplicaria o snackbar.
    useEffect(() => {
        if(errorMessage) {
            enqueueSnackbar(errorMessage, {
                variant: 'error',
            });
            dispatch(clearProfilesMessages());
        }
    }, [errorMessage]);

    const handleMouseMove = (e: React.MouseEvent) => {
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleActionsMenu = (row: any) => {
        setSelectedRow(row);
        setMenuPos({ left: lastMousePosRef.current.x, top: lastMousePosRef.current.y });
    };

    const handleCloseActionsMenu = () => {
        setMenuPos(null);
        setSelectedRow(null);
    };

    const handleOpenUserAssociation = useCallback((row: any) => {
        setSelectedProfileForUsers(row);
        setOpenUserAssociationPanel(true);
    }, []);


    const associateUsersDisabledReason = !isAuthorizedToViewUsers
        ? "Você não tem permissão para associar usuários"
        : selectedProfiles.length !== 1
            ? "Selecione exatamente um perfil para associar usuários"
            : "";

    const getActionMenuItems = (): ActionMenuItem[] => {
        if (!selectedRow) return [];

        return [
            {
                id: "edit",
                label: "Editar informações",
                icon: <EditOutlined fontSize="small" />,
                onClick: () => handleEditProfile(selectedRow),
                disabled: !isAuthorizedToSaveProfile
            },
            {
                id: "associateUsers",
                label: "Associar usuários",
                icon: <AccountCircleOutlined fontSize="small" />,
                onClick: () => handleOpenUserAssociation(selectedRow),
                disabled: !isAuthorizedToViewUsers
            },
            {
                id: "delete",
                label: "Excluir",
                icon: <DeleteOutlined fontSize="small" />,
                onClick: () => deleteItemAction(selectedRow),
                danger: true,
                dividerBefore: true,
                disabled: !isAuthorizedToDeleteProfile
            },
        ];
    };

    return (
        <Box sx={{marginRight: 5, marginLeft: 3}}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{mb: 2}}
            >
                <ASectionTitle
                    styleContainer={{padding: '0'}}
                    styleSubtitle={{marginBottom: 0, paddingTop: 0}}
                    styleContainerTitle={{paddingBottom: 0}}
                    contentChildrenStyle={{marginBottom: 0, marginTop: -2}}
                    titleLabel={'Perfis de acesso'}
                    subTitleLabel={'Gerencie os perfis de acesso associados a usuários e usinas.'}
                    contentChildren={
                        <Button
                            onClick={() => navigate(SystemRoutes.SETTINGS_PROFILE_FORM)}
                            disabled={!isAuthorizedToSaveProfile}
                            variant="contained"
                            color="primary"
                            startIcon={<AddCircleOutlineIcon/>}
                            sx={{
                                paddingTop: 1,
                                paddingBottom: 1,
                                marginBottom: 1,
                                borderRadius: '8px',
                                boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
                                textTransform: 'uppercase',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                letterSpacing: '0.02857em',
                                whiteSpace: 'nowrap',
                                height: 'fit-content'
                            }}
                        >
                            Novo perfil de acesso
                        </Button>
                    }
                />
            </Box>
            <ACard
                ariaLabel={"settings-profiles-page"}
                styleProps={{
                    headerStyle: {height: 0, padding: 0},
                    contentStyle: {marginBottom: 0, padding: 0},
                    cardStyle: {
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px',
                        margin: 0,
                        border: 'none',
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 3,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        height: 64
                    }}
                >
                    <Typography
                        variant="body2"
                        aria-live="polite"
                        sx={{
                            fontWeight: 500,
                            color: selectedProfiles.length > 0
                                ? theme.palette.text.primary
                                : theme.palette.text.secondary,
                            fontSize: "14px",
                        }}
                    >
                        {selectedProfiles.length} {selectedProfiles.length === 1 ? 'item selecionado' : 'itens selecionados'}
                    </Typography>

                    {/* O painel de associação trabalha com um perfil por vez. */}
                    <Tooltip title={associateUsersDisabledReason}>
                        {/* Botão desabilitado não dispara eventos: o span permite exibir o tooltip. */}
                        <span>
                            <Button
                                variant="text"
                                disabled={Boolean(associateUsersDisabledReason)}
                                onClick={() => handleOpenUserAssociation(selectedProfiles[0])}
                                endIcon={<AccountCircleOutlined/>}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: theme.palette.text.primary
                                }}
                            >
                                ASSOCIAR USUÁRIOS
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <Box onMouseMove={handleMouseMove}>
                    <ADataGrid
                        ariaLabel={"settings-profiles-grid"}
                        tableContainerStyle={{borderRadius: 0}}
                        hideFilters={false}
                        hideSelection={false}
                        columns={columns}
                        rows={rows}
                        loading={isLoading}
                        totalOfRecords={totalOfRecords}
                        page={pagedSearchParams.page}
                        rowsPerPage={pagedSearchParams.size}
                        onFiltersChange={handleOnFiltersChange}
                        onRowSelect={handleOnRowSelect}
                        onClearSelectedTempRows={clearSelection}
                        disableActionsHeaderIcons={true}
                        actions={
                            <>
                                <span></span>
                                <IconButton
                                    name="actions"
                                    size={"small"}
                                    aria-label="Ações"
                                    onClick={(row: any) => handleActionsMenu(row)}
                                >
                                    <Tooltip title="Opções">
                                        <MoreHorizOutlined/>
                                    </Tooltip>
                                </IconButton>
                            </>
                        }
                    />
                </Box>
            </ACard>
            <ConfirmDialog
                title={'Confirmar exclusão'}
                description={'Você tem certeza que deseja excluir esse perfil?'}
                open={openDialog}
                handleConfirm={confirmDialog}
                handleClose={closeDialog}
            />
            <ActionsMenu
                open={Boolean(menuPos && selectedRow)}
                onClose={handleCloseActionsMenu}
                menuPos={menuPos}
                items={getActionMenuItems()}
            />
            <UserAssociationPanel
                open={openUserAssociationPanel}
                onClose={() => setOpenUserAssociationPanel(false)}
                profileId={selectedProfileForUsers?.id || 0}
                profileName={selectedProfileForUsers?.name || ""}
            />
        </Box>
    );
}

export default function ProfilesSettingsPage() {
    return (
        <QueryClientProvider client={sharedQueryClient}>
            <ProfilesSettingsHomePage />
        </QueryClientProvider>
    )
}